import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../shared/config/routes'
import { SETTINGS_USER, MAIN_SECTIONS } from '../data/settingsData'
import { notificationPreferencesStore } from '../../../shared/stores/notificationPreferencesStore'
import { counselorSessionStore } from '../../../shared/stores/counselorSessionStore'
import { organizationContextStore } from '../../../shared/stores/organizationContextStore'
import { fetchAcademicCalendar } from 'api/academicCalendar'

const useSettingsPage = () => {
	const navigate = useNavigate()
	const [view, setView] = useState('main')
	const [profile, setProfile] = useState(SETTINGS_USER)
	const [profileEditing, setProfileEditing] = useState(false)
	const [studentsOpen, setStudentsOpen] = useState(false)
	const [academicYearLabel, setAcademicYearLabel] = useState(null)
	const [academicYearMetaStatus, setAcademicYearMetaStatus] = useState('idle')

	const canManageAcademicCalendar = counselorSessionStore.canAccessUserManagement

	useEffect(() => {
		notificationPreferencesStore.ensureLoaded().catch(() => {})
	}, [])

	useEffect(() => {
		let cancelled = false
		const loadAcademicMeta = async () => {
			if (!canManageAcademicCalendar) {
				setAcademicYearLabel(null)
				setAcademicYearMetaStatus('idle')
				return
			}
			setAcademicYearMetaStatus('loading')
			try {
				await counselorSessionStore.ensureLoaded()
				await organizationContextStore.ensureInitialized()
				if (!organizationContextStore.selectedOrganizationId) {
					if (!cancelled) {
						setAcademicYearLabel(null)
						setAcademicYearMetaStatus('error')
					}
					return
				}
				const response = await fetchAcademicCalendar()
				const data = response?.data ?? response
				if (!cancelled) {
					setAcademicYearLabel(data?.label || null)
					setAcademicYearMetaStatus('success')
				}
			} catch {
				if (!cancelled) {
					setAcademicYearLabel(null)
					setAcademicYearMetaStatus('error')
				}
			}
		}
		loadAcademicMeta()
		return () => {
			cancelled = true
		}
	}, [canManageAcademicCalendar])

	const notifications = notificationPreferencesStore.preferences
	const notificationsLoadStatus = notificationPreferencesStore.loadStatus

	const activeNotifications = useMemo(
		() => Object.values(notifications).filter(Boolean).length,
		[notifications],
	)

	const sections = useMemo(() => {
		if (canManageAcademicCalendar) return MAIN_SECTIONS
		return MAIN_SECTIONS.map(section => ({
			...section,
			rows: section.rows.filter(row => !row.adminOnly),
		})).filter(section => section.rows.length > 0)
	}, [canManageAcademicCalendar])

	const goToView = useCallback(
		nextView => {
			if (nextView === 'school-preferences' && !canManageAcademicCalendar) {
				setView('main')
				return
			}
			setView(nextView)
		},
		[canManageAcademicCalendar],
	)

	const goBack = useCallback(() => {
		setView('main')
	}, [])

	const onAcademicCalendarUpdated = useCallback(data => {
		setAcademicYearLabel(data?.label || null)
		setAcademicYearMetaStatus('success')
	}, [])

	useEffect(() => {
		if (view === 'school-preferences' && !canManageAcademicCalendar) {
			setView('main')
		}
	}, [view, canManageAcademicCalendar])

	const toggleNotification = useCallback(async id => {
		const current = notificationPreferencesStore.preferences[id]
		await notificationPreferencesStore.updatePreference(id, !current)
	}, [])

	const startProfileEdit = useCallback(() => {
		setProfileEditing(true)
	}, [])

	const cancelProfileEdit = useCallback(() => {
		setProfile(SETTINGS_USER)
		setProfileEditing(false)
	}, [])

	const saveProfile = useCallback(() => {
		setProfileEditing(false)
	}, [])

	const updateProfileName = useCallback(name => {
		setProfile(current => ({ ...current, name }))
	}, [])

	const showMoreStudents = useCallback(() => {
		setStudentsOpen(true)
	}, [])

	const onOpenParentCommunication = useCallback(() => {
		navigate(ROUTES.PARENT_COMMUNICATION)
	}, [navigate])

	const onOpenReports = useCallback(() => {
		navigate(ROUTES.REPORTS)
	}, [navigate])

	return {
		view,
		profile,
		profileEditing,
		studentsOpen,
		notifications,
		activeNotifications,
		notificationsLoadStatus,
		notificationsSaveStatus: notificationPreferencesStore.saveStatus,
		notificationsError: notificationPreferencesStore.error,
		sections,
		academicYearLabel,
		academicYearMetaStatus,
		canManageAcademicCalendar,
		goToView,
		goBack,
		toggleNotification,
		startProfileEdit,
		cancelProfileEdit,
		saveProfile,
		updateProfileName,
		showMoreStudents,
		onOpenParentCommunication,
		onOpenReports,
		onAcademicCalendarUpdated,
	}
}

export default useSettingsPage
